import { CompareDiv } from 'pages/v2/compiler/ComparePanelComponent';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

type CompareStatus = 'added' | 'updated' | 'removed' | 'equal';

type VersionCompareRowProps<T> = {
  data: { left: T; right: T };
  displayCondition?: (item: T) => boolean;
  compareBy?: (item: T) => any;
  compareByDom?: boolean;
  children: (props: { item: T; isLeft: boolean; status: CompareStatus; compareClassName: string }) => React.ReactNode;
};

function BaseVersionCompare<T>({ data, displayCondition, compareBy, compareByDom, children }: VersionCompareRowProps<T>) {
  const { left, right } = data;
  const leftContainerRef = useRef<HTMLDivElement>(null);
  const rightContainerRef = useRef<HTMLDivElement>(null);
  const isSyncing = useRef(false);

  const [compareStatus, setCompareStatus] = useState<CompareStatus>('equal');
  const showLeft = displayCondition === undefined || displayCondition(left);
  const showRight = displayCondition === undefined || displayCondition(right);

  useEffect(() => {
    if (!compareBy) return;

    const leftValue = left !== null ? compareBy(left) : undefined;
    const rightValue = right !== null ? compareBy(right) : undefined;

    let status: CompareStatus = 'equal';
    if ((leftValue === null || leftValue === undefined) && rightValue !== null) {
      status = 'added';
    } else if (
      leftValue !== null &&
      (rightValue === null || rightValue === undefined || (rightValue === '' && leftValue !== ''))
    ) {
      status = 'removed';
    } else if (JSON.stringify(leftValue) !== JSON.stringify(rightValue)) {
      status = 'updated';
    }

    setCompareStatus(status);
  }, [left, right]);

  useEffect(() => {
    if (!compareByDom) return;
    const leftEl = leftContainerRef.current;
    const rightEl = rightContainerRef.current;
    if (!leftEl || !rightEl) return;

    const doCompare = () => setCompareStatus(domCompare(leftEl, rightEl));
    const raf = requestAnimationFrame(doCompare);

    // in case primereact datatables render or load data asynchronously, use MutationObserver to re-compare on DOM changes
    const observer = new MutationObserver(doCompare);
    observer.observe(leftEl, { childList: true, subtree: true });
    observer.observe(rightEl, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
    };
  }, [compareByDom, left, right]);

  useEffect(() => {
    const leftContainer = leftContainerRef.current;
    const rightContainer = rightContainerRef.current;
    if (!leftContainer || !rightContainer) return;

    const abortController = new AbortController();
    const { signal } = abortController;

    const tryAttachScrollSync = () => {
      const leftScroll = leftContainer.querySelector('.p-datatable-wrapper') as HTMLElement | null;
      const rightScroll = rightContainer.querySelector('.p-datatable-wrapper') as HTMLElement | null;
      if (!leftScroll || !rightScroll) return false;

      const syncScroll = (source: HTMLElement, target: HTMLElement) => {
        if (isSyncing.current) return;
        isSyncing.current = true;
        target.scrollLeft = source.scrollLeft;
        requestAnimationFrame(() => (isSyncing.current = false));
      };

      const handleLeftScroll = () => syncScroll(leftScroll!, rightScroll!);
      const handleRightScroll = () => syncScroll(rightScroll!, leftScroll!);

      leftScroll.addEventListener('scroll', handleLeftScroll, { signal });
      rightScroll.addEventListener('scroll', handleRightScroll, { signal });

      return true;
    };

    // Try immediately in case elements are already rendered
    tryAttachScrollSync();
    // Observe both containers — when datatables render, observer will try again
    const observer = new MutationObserver(() => {
      tryAttachScrollSync();
    });
    observer.observe(leftContainer, { childList: true, subtree: true });
    observer.observe(rightContainer, { childList: true, subtree: true });

    signal.addEventListener('abort', () => observer.disconnect());

    return () => abortController.abort();
  }, [left, right]);

  if (!showLeft && !showRight) return null;

  const leftContent = showLeft
    ? children({ item: left, isLeft: true, status: compareStatus, compareClassName: `compare-left-${compareStatus}` })
    : null;

  const rightContent = showRight
    ? children({ item: right, isLeft: false, status: compareStatus, compareClassName: `compare-right-${compareStatus}` })
    : null;

  const isLeftEmpty = !leftContent;
  const isRightEmpty = !rightContent;

  return (
    <>
      <CompareSideContainer ref={leftContainerRef} className={isLeftEmpty && !isRightEmpty ? 'empty-side' : ''}>
        {leftContent}
      </CompareSideContainer>
      <CompareSideContainer ref={rightContainerRef} className={isRightEmpty && !isLeftEmpty ? 'empty-side' : ''}>
        {rightContent}
      </CompareSideContainer>
    </>
  );
}

const CompareSideContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 20px;
  height: 100%;

  &:empty {
    background-color: ${(props) => props.theme.colors.elementsInputBorder};
    min-height: 100%;
    /* display: none; */
  }

  &.empty-side {
    background-color: ${(props) => props.theme.colors.elementsInputBorder};
    width: 100%;
    height: 100%;
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .compare-left-added {
    background-color: ${(props) => props.theme.colors.elementsInputBorder};
    .p-datatable-header,
    .p-datatable .p-datatable-header,
    .p-datatable-thead > tr > th {
      background-color: #cc302b80;
    }
    .p-datatable-table .p-datatable-tbody > tr,
    tfoot > tr > td.p-row-total,
    .p-datatable-table .p-datatable-tfoot tr > td,
    .p-datatable-tbody > tr > td:last-child,
    .p-datatable-tfoot > tr > td:last-child,
    tfoot > tr > td.p-row-note {
      background-color: ${(props) => props.theme.colors.statusLabelRedBg};
    }
  }

  .compare-left-updated,
  .compare-left-removed {
    background-color: ${(props) => props.theme.colors.statusLabelRedBg};

    .p-datatable-header,
    .p-datatable .p-datatable-header,
    .p-datatable-thead > tr > th {
      background-color: #cc302b80;
    }
    .p-datatable-table .p-datatable-tbody > tr,
    tfoot > tr > td.p-row-total,
    .p-datatable-table .p-datatable-tfoot tr > td,
    .p-datatable-tbody > tr > td:last-child,
    .p-datatable-tfoot > tr > td:last-child,
    tfoot > tr > td.p-row-note {
      background-color: ${(props) => props.theme.colors.statusLabelRedBg};
    }
  }

  .compare-right-removed {
    background-color: ${(props) => props.theme.colors.statusLabelRedBg};

    .p-datatable-header,
    .p-datatable .p-datatable-header,
    .p-datatable-thead > tr > th {
      background-color: #1e605299 !important;
    }
    &&&.p-datatable-table .p-datatable-tbody > tr > td {
      background-color: ${(props) => props.theme.colors.secondaryLightGreen};
    }

    .p-datatable-table .p-datatable-tbody > tr,
    tfoot > tr > td.p-row-total,
    .p-datatable-table .p-datatable-tfoot tr > td,
    .p-datatable-tbody > tr > td:last-child,
    .p-datatable-tfoot > tr > td:last-child,
    tfoot > tr > td.p-row-note {
      background-color: ${(props) => props.theme.colors.secondaryLightGreen};
    }
  }

  .compare-right-added {
    background-color: ${(props) => props.theme.colors.secondaryLightGreen};

    .p-datatable-header,
    .p-datatable .p-datatable-header,
    .p-datatable-thead > tr > th {
      background-color: #1e605299;
    }
    .p-datatable-table .p-datatable-tbody > tr,
    tfoot > tr > td.p-row-total,
    .p-datatable-table .p-datatable-tfoot tr > td,
    .p-datatable-tbody > tr > td:last-child,
    .p-datatable-tfoot > tr > td:last-child,
    tfoot > tr > td.p-row-note {
      background-color: ${(props) => props.theme.colors.secondaryLightGreen};
    }
  }
  .compare-right-updated {
    background-color: ${(props) => props.theme.colors.secondaryLightGreen};

    .p-datatable-header,
    .p-datatable .p-datatable-header,
    .p-datatable-thead > tr > th {
      background-color: #1e605299;
    }
    .p-datatable-table .p-datatable-tbody > tr,
    tfoot > tr > td.p-row-total,
    .p-datatable-table .p-datatable-tfoot tr > td,
    .p-datatable-tbody > tr > td:last-child,
    .p-datatable-tfoot > tr > td:last-child,
    tfoot > tr > td.p-row-note {
      background-color: ${(props) => props.theme.colors.secondaryLightGreen};
    }
  }
`;

export function VersionCompareRow<T>(props: VersionCompareRowProps<T>) {
  return <BaseVersionCompare {...props} />;
}

export function VersionCompareSection<T>(props: VersionCompareRowProps<T>) {
  return (
    <CompareDiv>
      <BaseVersionCompare {...props} />
    </CompareDiv>
  );
}

function domCompare(a: Node, b: Node): CompareStatus {
  let result: CompareStatus = 'equal';

  if (!isEqualShallow(a, b)) {
    return 'updated';
  }
  const aChildren = Array.from(a.childNodes).filter(
    (n) => !(n.nodeType === Node.TEXT_NODE && n.textContent?.trim().length === 0)
  );

  const bChildren = Array.from(b.childNodes).filter(
    (n) => !(n.nodeType === Node.TEXT_NODE && n.textContent?.trim().length === 0)
  );

  const fullyEqualPairs = collectPairs(aChildren, bChildren, isEqualDeeply);

  const underQuestionA = aChildren.filter((_, i) => !fullyEqualPairs.some((p) => p.indexInA === i && p.indexInB !== -1));
  const underQuestionB = bChildren.filter((_, i) => !fullyEqualPairs.some((p) => p.indexInA !== -1 && p.indexInB === i));
  const shallowEqualPairs = collectPairs(underQuestionA, underQuestionB, isEqualShallow);

  const removed = shallowEqualPairs.filter((f) => f.indexInB === -1).map((f) => underQuestionA[f.indexInA]);
  if (removed?.some((x) => !!x)) {
    return 'removed';
  }

  const added = shallowEqualPairs.filter((f) => f.indexInA === -1).map((f) => underQuestionB[f.indexInB]);
  if (added?.some((x) => !!x)) {
    return 'added';
  }

  const maybeChanged = shallowEqualPairs.filter((f) => f.indexInA !== -1 && f.indexInB !== -1);
  for (const x of maybeChanged) {
    result = domCompare(underQuestionA[x.indexInA], underQuestionB[x.indexInB]);
  }

  return result;
}

function collectPairs(aChildren: Node[], bChildren: Node[], equalityComparer: (a: Node, b: Node) => boolean) {
  const pairs: { indexInA: number; indexInB: number }[] = [];
  for (let i = 0; i < aChildren.length; i++) {
    const pairInB = bChildren.findIndex(
      (bb, bi) => !pairs.some((v) => v.indexInB === bi) && equalityComparer(aChildren[i], bb)
    );
    pairs.push({ indexInA: i, indexInB: pairInB });
  }
  for (let i = 0; i < bChildren.length; i++) {
    if (pairs.some((p) => p.indexInB === i)) {
      continue;
    }

    const pairInA = aChildren.findIndex(
      (aa, ai) => !pairs.some((v) => v.indexInA === ai) && equalityComparer(bChildren[i], aa)
    );
    pairs.push({ indexInA: pairInA, indexInB: i });
  }
  return pairs;
}

function isEqualShallow(a: Node, b: Node) {
  if (shouldSkipComparison(a) || shouldSkipComparison(b)) {
    return true;
  }

  if (a.nodeType !== b.nodeType) {
    return false;
  }

  if (a.nodeType === Node.TEXT_NODE) {
    if ((a.textContent ?? '').trim() !== (b.textContent ?? '').trim()) {
      return false;
    }

    return true;
  }

  if (a.nodeType === Node.ELEMENT_NODE) {
    if (differentAttrs(a, b)) {
      return false;
    }
  }

  return true;
}

function isEqualDeeply(a: Node, b: Node) {
  if (shouldSkipComparison(a) || shouldSkipComparison(b)) {
    return true;
  }

  if (!isEqualShallow(a, b)) {
    return false;
  }

  const aChildren = Array.from(a.childNodes).filter(
    (n) => !(n.nodeType === Node.TEXT_NODE && n.textContent?.trim().length === 0)
  );

  const bChildren = Array.from(b.childNodes).filter(
    (n) => !(n.nodeType === Node.TEXT_NODE && n.textContent?.trim().length === 0)
  );

  if (aChildren.length !== bChildren.length) {
    return false;
  }

  for (let i = 0; i < aChildren.length; i++) {
    if (!isEqualDeeply(aChildren[i], bChildren[i])) {
      return false;
    }
  }

  return true;
}

function differentAttrs(a: Node, b: Node): boolean {
  const el1 = a as HTMLElement;
  const el2 = b as HTMLElement;

  const ignoredPrefixes = ['parent', 'state', 'pr_id_'];
  const ignoredExact = new Set(['id', 'class', 'style']);

  const isIgnored = (name: string, value: string | null): boolean => {
    return (
      ignoredExact.has(name) ||
      ignoredPrefixes.some((prefix) => name.startsWith(prefix)) ||
      (name === 'aria-labelledby' && !!value && value.startsWith('pr_id_'))
    );
  };

  const haveDifferentAttributes = (from: HTMLElement, to: HTMLElement): boolean => {
    for (const { name, value } of Array.from(from.attributes)) {
      if (isIgnored(name, value)) continue;
      if (to.getAttribute(name) !== value) return true;
    }
    return false;
  };

  return haveDifferentAttributes(el1, el2) || haveDifferentAttributes(el2, el1);
}

function shouldSkipComparison(n: Node): boolean {
  return n.nodeType === Node.ELEMENT_NODE && (n as HTMLElement).hasAttribute('data-skip-compare');
}
